import { useEffect, useMemo, useRef, useState } from "react";
import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { MessageCircle, Paperclip, Pause, Play, RefreshCw, RotateCcw, Search, Send, FileText, X } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { chatbotApi, chatbotError, type ChatMessage, type Conversation } from "./chatbot-api";
import { templatesApi } from "../whatsapp-templates/api";
import { Dropdown } from "../../components/ui/dropdown";

const button = "rounded-lg border border-line bg-elevated px-3 py-2 text-sm font-semibold text-ink hover:bg-soft disabled:opacity-40";

function threadName(thread: Conversation) {
  const name = String(thread.fields?.name ?? "").trim();
  if (name && name !== "?" && name.toLowerCase() !== "unknown") return name;
  // No saved name yet (AI hasn't captured it) — same label as the
  // reference inbox. Real names appear here automatically once known.
  return "Valued Client";
}

function shortTime(iso: string) {
  try {
    const d = new Date(iso);
    return d.toLocaleString([], { month: "numeric", day: "numeric", hour: "numeric", minute: "2-digit" });
  } catch {
    return "";
  }
}

function MediaView({ message }: { message: ChatMessage }) {
  const media = message.media;
  if (!media?.url) return null;
  if (media.type === "image") {
    return <a href={media.url} target="_blank" rel="noreferrer"><img src={media.url} alt={media.filename || "attachment"} className="max-h-64 max-w-full rounded-lg object-contain" /></a>;
  }
  if (media.type === "video") {
    return <video src={media.url} controls className="max-h-64 max-w-full rounded-lg" />;
  }
  if (media.type === "audio") {
    return <audio src={media.url} controls className="w-full" />;
  }
  return <a href={media.url} target="_blank" rel="noreferrer" className="text-xs font-semibold text-brand underline">{media.filename || "Download attachment"}</a>;
}

export function ChatbotHistory() {
  const { token } = useAuth() as any;
  const cache = useQueryClient();
  const [params, setParams] = useSearchParams();
  const agentId = params.get("agent") || "";
  const templateId = params.get("template") || "";
  const instanceName = params.get("instance") || "";
  const [depth, setDepth] = useState(1);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [msgDepth, setMsgDepth] = useState(1);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [search, setSearch] = useState("");
  const [unread, setUnread] = useState<Set<string>>(new Set());
  const [draft, setDraft] = useState("");
  const [composerTpl, setComposerTpl] = useState<{ id: string; buttons: { type: string; text: string; value: string; followup_body: string }[] } | null>(null);
  const [attach, setAttach] = useState<File | null>(null);
  const [sending, setSending] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [sendError, setSendError] = useState("");
  const [showTpl, setShowTpl] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const fileRef = useRef<HTMLInputElement | null>(null);
  const prevUpdated = useRef<Map<string, string>>(new Map());
  const listRef = useRef<HTMLDivElement | null>(null);
  const msgListRef = useRef<HTMLDivElement | null>(null);
  // True while the user sits at the newest messages; false once they scroll
  // up to read history. New arrivals only auto-scroll when stuck.
  const stickRef = useRef(true);
  const growTopRef = useRef<number | null>(null);

  const agents = useQuery({ queryKey: ["chatbot-agents", token], queryFn: () => chatbotApi.agents(token), enabled: Boolean(token) });
  // Chat-scoped number inventory (works for members; the admin endpoint needs
  // settings.general and would come back empty here).
  const connections = useQuery({
    queryKey: ["chatbot-instances", token],
    queryFn: () => chatbotApi.instances(token),
    enabled: Boolean(token),
  });
  // Infinite scroll: fetch `depth` pages and accumulate. New threads land on
  // page 0, so a background poll keeps the top fresh while scroll loads more.
  const threads = useQuery({
    queryKey: ["chatbot-history", token, agentId, templateId, instanceName, depth],
    queryFn: async () => {
      let total = 0;
      const all: Conversation[] = [];
      const seen = new Set<string>();
      for (let p = 0; p < depth; p++) {
        const r = templateId || instanceName
          ? await chatbotApi.conversations(token, agentId, p * 50, templateId, instanceName)
          : await chatbotApi.conversations(token, agentId, p * 50);
        if (p === 0) total = r.total;
        for (const t of r.conversations) {
          if (!seen.has(t.id)) {
            seen.add(t.id);
            all.push(t);
          }
        }
        if (r.conversations.length < 50) break;
      }
      return { conversations: all, total };
    },
    enabled: Boolean(token),
    refetchInterval: 5000,
    placeholderData: keepPreviousData,
  });
  const loadedCount = threads.data?.conversations.length ?? 0;
  const totalCount = threads.data?.total ?? 0;
  const hasMore = loadedCount < totalCount;

  useEffect(() => {
    if (!loadingMore) return;
    if (!threads.isFetching) setLoadingMore(false);
  }, [loadingMore, loadedCount, threads.isFetching]);

  const onListScroll = () => {
    const el = listRef.current;
    if (!el || threads.isFetching || loadingMore || !hasMore) return;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 240) {
      setLoadingMore(true);
      setDepth(d => d + 1);
    }
  };

  const selected: Conversation | null = useMemo(
    () => threads.data?.conversations.find(t => t.id === selectedId) ?? null,
    [threads.data, selectedId],
  );

  const messages = useQuery({
    queryKey: ["chatbot-messages", token, selected?.id, msgDepth],
    queryFn: async () => {
      // Newest page first; accumulate so scroll-up loads older history.
      const all: ChatMessage[] = [];
      const seen = new Set<string>();
      let hasOlder = false;
      for (let p = 0; p < msgDepth; p++) {
        const rows = await chatbotApi.messages(token, selected!.id, p * 50);
        for (const m of rows) {
          if (!seen.has(m.id)) {
            seen.add(m.id);
            all.push(m);
          }
        }
        if (rows.length < 50) break;
        if (p === msgDepth - 1) hasOlder = true;
      }
      return { messages: all, hasOlder };
    },
    enabled: Boolean(token && selected),
    refetchInterval: 5000,
    placeholderData: keepPreviousData,
  });
  const msgRows = useMemo(() => messages.data?.messages ?? [], [messages.data?.messages]);
  const msgHasOlder = messages.data?.hasOlder ?? false;

  const templates = useQuery({
    queryKey: ["whatsapp-templates", token],
    queryFn: templatesApi.list,
    enabled: Boolean(token),
    staleTime: 60000,
  });

  const insertTemplate = (tpl: { template_id: string; name: string; body: string; buttons?: { type: string; text: string; value?: string }[] }) => {
    setDraft(prev => (prev.trim() ? `${prev.trim()}\n\n${tpl.body}` : tpl.body));
    // Snapshot the template's buttons (with linked follow-up bodies resolved
    // from the library) so button taps later answer deterministically.
    const library = templates.data ?? [];
    setComposerTpl({
      id: tpl.template_id,
      buttons: (tpl.buttons ?? []).slice(0, 5).map(b => {
        const followup = b.type === "TEMPLATE"
          ? (library.find(t => t.template_id === (b.value || "") || t.name === (b.value || ""))?.body ?? "")
          : "";
        return { type: b.type, text: b.text, value: b.value || "", followup_body: followup.slice(0, 4096) };
      }),
    });
    setShowTpl(false);
    setSendError("");
  };

  const agentName = (thread: Conversation) =>
    agents.data?.find(a => a.id === thread.agent_id)?.name || thread.agent_name || "WhatsApp AI agent";

  // Sort newest first so new messages jump to the top of the list.
  const sorted = useMemo(() => {
    const list = [...(threads.data?.conversations ?? [])];
    list.sort((a, b) => +new Date(b.updated_at) - +new Date(a.updated_at));
    const needle = search.trim().toLowerCase();
    if (!needle) return list;
    return list.filter(t =>
      threadName(t).toLowerCase().includes(needle) ||
      (t.phone || "").includes(needle),
    );
  }, [threads.data, search]);

  // Auto-select first thread on load.
  useEffect(() => {
    if (!selectedId && sorted.length > 0) setSelectedId(sorted[0].id);
  }, [sorted, selectedId]);

  // Fresh filter (e.g. browser back/forward) restarts the list.
  // Single-filter mode: only one of agent / template / instance applies.
  const filterKey = `${agentId}|${templateId}|${instanceName}`;
  const prevFilterKey = useRef(filterKey);
  useEffect(() => {
    if (prevFilterKey.current !== filterKey) {
      prevFilterKey.current = filterKey;
      setDepth(1);
      setSelectedId(null);
      setUnread(new Set());
      prevUpdated.current.clear();
    }
  }, [filterKey]);

  // Detect new / updated threads since last poll -> mark unread (badge).
  useEffect(() => {
    const list = threads.data?.conversations ?? [];
    if (list.length === 0) return;
    setUnread(prev => {
      const next = new Set(prev);
      for (const t of list) {
        const old = prevUpdated.current.get(t.id);
        if (old === undefined) {
          // First load: don't badge everything, just record.
          prevUpdated.current.set(t.id, t.updated_at);
        } else if (old !== t.updated_at && t.id !== selectedId) {
          next.add(t.id);
          prevUpdated.current.set(t.id, t.updated_at);
        }
      }
      return next;
    });
  }, [threads.data, selectedId]);

  // Opening a chat clears its badge.
  const openThread = (thread: Conversation) => {
    setSelectedId(thread.id);
    setMsgDepth(1);
    setLoadingOlder(false);
    stickRef.current = true;
    setShowTpl(false);
    setDraft("");
    setComposerTpl(null);
    setAttach(null);
    setSendError("");
    prevUpdated.current.set(thread.id, thread.updated_at);
    setUnread(prev => {
      if (!prev.has(thread.id)) return prev;
      const next = new Set(prev);
      next.delete(thread.id);
      return next;
    });
  };

  const togglePause = async () => {
    if (!selected || toggling) return;
    setToggling(true);
    try {
      await chatbotApi.setAiPaused(token, selected.id, !selected.ai_paused);
      await cache.invalidateQueries({ queryKey: ["chatbot-history", token] });
      await threads.refetch();
    } catch (err) {
      setSendError(chatbotError(err));
    } finally {
      setToggling(false);
    }
  };

  const sendHuman = async () => {
    if (!selected || sending) return;
    const text = draft.trim();
    if (!text && !attach) return;
    if (attach && attach.size > 10 * 1024 * 1024) {
      setSendError("File is larger than 10 MB");
      return;
    }
    setSending(true);
    setSendError("");
    try {
      await chatbotApi.sendHumanMessage(token, selected.id, {
        text, file: attach,
        templateId: composerTpl?.id,
        buttons: composerTpl?.buttons,
      });
      setDraft("");
      setComposerTpl(null);
      setAttach(null);
      stickRef.current = true;
      if (fileRef.current) fileRef.current.value = "";
      await cache.invalidateQueries({ queryKey: ["chatbot-messages", token, selected.id] });
      await messages.refetch();
      await threads.refetch();
    } catch (err) {
      setSendError(chatbotError(err));
    } finally {
      setSending(false);
    }
  };

  // Oldest-first for display (API returns newest-first pages).
  const ordered = useMemo(() => [...msgRows].reverse(), [msgRows]);

  // Scroll to top of the message pane loads older history (chat-style,
  // no pager buttons). The list grows upward, so the view is pinned back
  // to the same message instead of jumping.
  const onMsgScroll = () => {
    const el = msgListRef.current;
    if (!el) return;
    stickRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 120;
    if (el.scrollTop > 200 || messages.isFetching || loadingOlder || !msgHasOlder) return;
    growTopRef.current = el.scrollHeight;
    setLoadingOlder(true);
    setMsgDepth(d => d + 1);
  };

  useEffect(() => {
    if (!loadingOlder) return;
    if (!messages.isFetching) setLoadingOlder(false);
  }, [loadingOlder, ordered.length, messages.isFetching]);

  // Keep the view glued to the newest message only while stuck there;
  // while reading history (or after older pages prepend), hold position.
  useEffect(() => {
    const el = msgListRef.current;
    if (!el) return;
    if (stickRef.current) {
      el.scrollTop = el.scrollHeight;
    } else if (growTopRef.current != null) {
      el.scrollTop = el.scrollHeight - growTopRef.current;
      growTopRef.current = null;
    }
  }, [ordered.length, selected?.id]);

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3">
      {/* Both filters always stay visible so either can be changed or cleared. */}
      <div className="flex shrink-0 flex-wrap items-center justify-between gap-3 rounded-xl border border-line bg-elevated p-4 shadow-sm">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-ink">Chatbot agent</span>
            <div className="w-48 shrink-0">
              <Dropdown
                ariaLabel="Chatbot agent filter"
                size="sm"
                searchable
                value={agentId}
                onChange={(val) => {
                  const next = new URLSearchParams(params);
                  if (val) next.set("agent", val);
                  else next.delete("agent");
                  next.delete("template");
                  next.delete("instance");
                  setParams(next);
                  setDepth(1);
                  setSelectedId(null);
                  setUnread(new Set());
                  prevUpdated.current.clear();
                }}
                options={[
                  { value: "", label: "All chatbot agents" },
                  ...(agents.data?.map(agent => ({ value: agent.id, label: agent.name })) ?? []),
                  ...(agentId && !agents.data?.some(a => a.id === agentId) ? [{ value: agentId, label: "Selected agent" }] : []),
                ]}
              />
            </div>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-ink">Template</span>
            <div className="w-48 shrink-0">
              <Dropdown
                ariaLabel="Template filter"
                size="sm"
                searchable
                value={templateId}
                onChange={(val) => {
                  const next = new URLSearchParams(params);
                  if (val) next.set("template", val);
                  else next.delete("template");
                  next.delete("agent");
                  next.delete("instance");
                  setParams(next);
                  setDepth(1);
                  setSelectedId(null);
                  setUnread(new Set());
                  prevUpdated.current.clear();
                }}
                options={[
                  { value: "", label: "All templates" },
                  ...(templates.data?.map(t => ({ value: t.template_id, label: t.name })) ?? []),
                  ...(templateId && !templates.data?.some(t => t.template_id === templateId) ? [{ value: templateId, label: "Selected template" }] : []),
                ]}
              />
            </div>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-ink">WhatsApp number</span>
            <div className="w-52 shrink-0">
              <Dropdown
                ariaLabel="WhatsApp number filter"
                size="sm"
                searchable
                value={instanceName}
                onChange={(val) => {
                  const next = new URLSearchParams(params);
                  if (val) next.set("instance", val);
                  else next.delete("instance");
                  next.delete("agent");
                  next.delete("template");
                  setParams(next);
                  setDepth(1);
                  setSelectedId(null);
                  setUnread(new Set());
                  prevUpdated.current.clear();
                }}
                options={[
                  { value: "", label: "All numbers" },
                  ...((connections.data ?? []).filter(c => !!c.instance_name).map(c => ({
                    value: c.instance_name,
                    label: c.phone_number ? `+${c.phone_number} · ${c.instance_name}` : c.instance_name,
                  }))),
                  ...(instanceName && !(connections.data ?? []).some(c => c.instance_name === instanceName) ? [{ value: instanceName, label: "Selected number" }] : []),
                ]}
              />
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-ink-3">{totalCount} conversations</span>
          <button
            className={button}
            title="Reset filters"
            aria-label="Reset filters"
            onClick={() => {
              setParams(new URLSearchParams());
              setSearch("");
              setDepth(1);
              setSelectedId(null);
              setUnread(new Set());
              prevUpdated.current.clear();
            }}
          >
            <RotateCcw className="h-4 w-4" />
          </button>
          <button className={button} aria-label="Refresh conversations" onClick={() => threads.refetch()}>
            <RefreshCw className={`h-4 w-4 ${threads.isFetching ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {threads.isError && <p role="alert" className="rounded-lg bg-danger-soft p-4 text-sm text-danger">{chatbotError(threads.error)} <button className="underline" onClick={() => threads.refetch()}>Retry</button></p>}

      <div className="flex min-h-[420px] flex-1 overflow-hidden rounded-xl border border-line bg-elevated shadow-sm">
        {/* ── Left: chat list ── */}
        <div className="flex w-72 shrink-0 flex-col border-r border-line sm:w-80">
          <div className="flex items-center gap-2 border-b border-line p-3">
            <div className="relative min-w-0 flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-3" />
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search name or number..."
                className="w-full rounded-lg border border-line bg-soft py-2 pl-9 pr-3 text-sm text-ink outline-none focus:ring-1 focus:ring-inset focus:ring-brand"
              />
            </div>
          </div>
          <div ref={listRef} onScroll={onListScroll} className="min-h-0 flex-1 overflow-y-auto">
            {threads.isLoading && loadedCount === 0 && <p className="p-6 text-center text-sm text-ink-3">Loading conversations...</p>}
            {!threads.isLoading && sorted.length === 0 && (
              <p className="p-6 text-center text-sm text-ink-3">No chatbot conversations yet. Messages will appear here after a customer contacts a connected agent.</p>
            )}
            {sorted.map(thread => {
              const active = thread.id === selectedId;
              const isUnread = unread.has(thread.id);
              const name = threadName(thread);
              const outcomes = Object.entries(thread.fields || {}).filter(([k, v]) => k !== "name" && v !== null && v !== undefined);
              return (
                <div
                  key={thread.id}
                  onClick={() => openThread(thread)}
                  className={`flex w-full cursor-pointer items-start gap-3 border-b border-line px-3 py-3 text-left transition-colors ${active ? "bg-accent-50 dark:bg-accent-500/10" : "hover:bg-soft/60"}`}
                >
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-soft text-sm font-bold text-ink-2">
                    {name.charAt(0).toUpperCase()}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="truncate text-sm font-semibold text-ink">{name}</span>
                      <span className="shrink-0 text-[10px] text-ink-3">{shortTime(thread.updated_at)}</span>
                    </div>
                    <div className="mt-0.5 flex items-center justify-between gap-2">
                      <span className="truncate text-xs text-ink-3">{thread.phone} · {thread.stage}</span>
                      {isUnread && (
                        <span className="flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full bg-danger px-1.5 text-[10px] font-bold text-white">1</span>
                      )}
                    </div>
                    {outcomes.length > 0 && (
                      <div className="mt-1.5 flex flex-wrap gap-1">
                        {outcomes.map(([key, val]) => (
                          <span key={key} className="rounded bg-soft px-1.5 py-0.5 text-[10px] text-ink-3 font-mono">
                            {key}: <span className="font-semibold text-ink">{String(val)}</span>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    className="sr-only"
                    onClick={(e) => { e.stopPropagation(); openThread(thread); }}
                  >
                    View
                  </button>
                </div>
              );
            })}
            {(loadingMore || threads.isFetching) && hasMore && (
              <p className="p-3 text-center text-xs text-ink-3">Loading more...</p>
            )}
          </div>
          <div className="border-t border-line px-3 py-2 text-center text-[11px] text-ink-3">
            Showing {loadedCount} of {totalCount}
          </div>
        </div>

        {/* ── Right: conversation ── */}
        <div className="flex min-w-0 flex-1 flex-col" style={{ background: "#efeae2" }}>
          {!selected ? (
            <div className="flex flex-1 flex-col items-center justify-center p-8 text-center">
              <MessageCircle className="mb-3 h-10 w-10 text-ink-3" />
              <p className="text-sm text-ink-2">Select a conversation from the list.</p>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-3 border-b border-line bg-elevated px-4 py-3">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#d9fdd3] text-sm font-bold text-ink">
                  {threadName(selected).charAt(0).toUpperCase()}
                </span>
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-ink">{threadName(selected)}</p>
                  <p className="truncate text-xs text-ink-3">WhatsApp · {selected.phone} · {agentName(selected)}</p>
                </div>
                <span className={`shrink-0 rounded px-2 py-0.5 text-xs font-semibold ${selected.stage === "qualified" ? "bg-success-soft text-success" : "bg-soft text-ink-2"}`}>
                  {selected.stage}
                </span>
                <button
                  onClick={togglePause}
                  disabled={toggling}
                  title={selected.ai_paused ? "Resume AI replies" : "Pause AI (human takeover)"}
                  className={`flex shrink-0 items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs font-semibold ${selected.ai_paused ? "border-brand bg-brand-soft text-brand" : "border-line bg-elevated text-ink-2 hover:bg-soft"}`}
                >
                  {selected.ai_paused ? <><Play className="h-3.5 w-3.5" />Resume AI</> : <><Pause className="h-3.5 w-3.5" />Take over</>}
                </button>
              </div>
              {selected.ai_paused && (
                <p className="border-b border-line bg-brand-soft/40 px-4 py-1.5 text-[11px] font-medium text-brand">
                  AI is paused on this chat — your messages go straight to the customer.
                </p>
              )}

              <div ref={msgListRef} onScroll={onMsgScroll} className="min-h-0 flex-1 overflow-y-auto px-4 py-4 sm:px-8">
                {(messages.isLoading || messages.isPending) && msgRows.length === 0 && <p className="py-10 text-center text-sm text-ink-3">Loading transcript...</p>}
                {messages.isError && <p role="alert" className="py-10 text-center text-sm text-danger">Could not load transcript. <button className="underline" onClick={() => messages.refetch()}>Retry</button></p>}
                {!messages.isLoading && ordered.length === 0 && (
                  <p className="py-16 text-center text-sm text-ink-2">No messages yet. The first one you send starts the conversation.</p>
                )}
                {(loadingOlder || (messages.isFetching && msgHasOlder)) && ordered.length > 0 && (
                  <p className="pb-2 text-center text-[11px] text-ink-3">Loading older messages...</p>
                )}
                <div className="space-y-3">
                  {ordered.map(message => message.sender === "human" ? (
                    <div key={message.id} className="ml-auto w-fit max-w-[75%] rounded-lg bg-brand-soft p-3 shadow-sm">
                      <p className="mb-1 text-[11px] font-semibold text-brand">You</p>
                      <MediaView message={message} />
                      {message.text && <p className="whitespace-pre-wrap break-words text-sm text-ink">{message.text}</p>}
                      <p className="mt-1 text-right text-[10px] text-ink-3">
                        {new Date(message.created_at).toLocaleString()}{message.status === "failed" ? " · Failed" : message.status === "queued" ? " · Sending…" : ""}
                      </p>
                    </div>
                  ) : (
                    <div key={message.id} className="space-y-2">
                      <div className="mr-auto w-fit max-w-[75%] rounded-lg bg-white p-3 shadow-sm">
                        <MediaView message={message} />
                        <p className="whitespace-pre-wrap break-words text-sm text-ink">{message.text}</p>
                        <p className="mt-1 text-right text-[10px] text-ink-3">{new Date(message.created_at).toLocaleString()}</p>
                      </div>
                      {message.reply && (
                        <div className="ml-auto w-fit max-w-[75%] rounded-lg p-3 shadow-sm" style={{ background: "#d9fdd3" }}>
                          <p className="whitespace-pre-wrap break-words text-sm text-ink">{message.reply}</p>
                          <p className="mt-1 text-right text-[10px] text-ink-3">
                            {new Date(message.created_at).toLocaleString()}{message.status === "uncertain" ? " · Delivery uncertain" : ""}
                          </p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <div className="relative px-4 py-2" style={{ background: "#efeae2" }}>
                {showTpl && (
                  <div className="absolute inset-x-4 bottom-full z-20 mb-2 max-h-64 overflow-y-auto rounded-xl border border-line bg-elevated shadow-xl">
                    {(templates.data ?? []).length === 0 && (
                      <p className="p-4 text-xs text-ink-3">No templates yet. Create one in WhatsApp › Template.</p>
                    )}
                    {(templates.data ?? []).map(t => (
                      <button
                        key={t.template_id}
                        onClick={() => insertTemplate(t)}
                        className="block w-full border-b border-line px-4 py-2.5 text-left last:border-0 hover:bg-soft"
                      >
                        <span className="block font-mono text-xs font-bold text-ink">{t.name}</span>
                        <span className="mt-0.5 line-clamp-2 block text-xs text-ink-3">{t.body}</span>
                      </button>
                    ))}
                  </div>
                )}
                {sendError && <p role="alert" className="mb-2 rounded-lg bg-danger-soft px-3 py-2 text-xs text-danger">{sendError}</p>}
                {composerTpl && (
                  <div className="mt-2 flex items-center gap-2 rounded-lg bg-brand-soft px-3 py-1.5 text-xs text-brand">
                    <FileText className="h-3.5 w-3.5 shrink-0" />
                    <span className="truncate">Template attached — button taps will answer from it</span>
                    <button onClick={() => setComposerTpl(null)} className="ml-auto rounded p-1 hover:bg-line" aria-label="Detach template">
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                )}
                {attach && (
                  <div className="mt-2 flex items-center gap-2 rounded-lg bg-soft px-3 py-2 text-xs text-ink">
                    <Paperclip className="h-3.5 w-3.5 shrink-0" />
                    <span className="truncate">{attach.name} ({(attach.size / 1024).toFixed(0)} KB)</span>
                    <button onClick={() => { setAttach(null); if (fileRef.current) fileRef.current.value = ""; }} className="ml-auto rounded p-1 hover:bg-line" aria-label="Remove attachment">
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                )}
                <div className="mt-2 flex items-center gap-1 rounded-full border border-line bg-soft px-1.5 py-1.5">
                  <input ref={fileRef} type="file" className="hidden" accept="image/jpeg,image/png,image/webp,audio/*,video/mp4,.pdf,.doc,.docx,.txt" onChange={e => { setAttach(e.target.files?.[0] ?? null); setSendError(""); }} />
                  <button onClick={() => { setShowTpl(v => !v); }} className={`shrink-0 rounded-full p-2 ${showTpl ? "bg-brand-soft text-brand" : "text-ink-2 hover:bg-line"}`} title="Insert template" aria-label="Insert template">
                    <FileText className="h-4 w-4" />
                  </button>
                  <button onClick={() => fileRef.current?.click()} className="shrink-0 rounded-full p-2 text-ink-2 hover:bg-line" title="Attach media" aria-label="Attach media">
                    <Paperclip className="h-4 w-4" />
                  </button>
                  <input
                    value={draft}
                    onChange={e => setDraft(e.target.value)}
                    onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendHuman(); } }}
                    placeholder="Type a message"
                    maxLength={4096}
                    className="min-w-0 flex-1 bg-transparent px-1 py-2 text-sm text-ink outline-none placeholder:text-ink-3"
                  />
                  <button
                    onClick={sendHuman}
                    disabled={sending || (!draft.trim() && !attach)}
                    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-brand text-white hover:bg-brand-hover disabled:opacity-40"
                    title="Send"
                    aria-label="Send message"
                  >
                    <Send className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
